/**
 * Broker Positions — open-positions blotter for the mt-ea bridge, the
 * companion to the Broker Ticket widget (typical ticket/blotter split).
 * Beta-only (registered with betaPreview in widgets.ts).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import clsx from "clsx";
import { closePosition } from "@/shared/api/mt-ea";
import { newIdempotencyKey } from "@/shared/api/mt-ea/idempotency";
import { useBrokerStore } from "@/stores/BrokerStore";

const fmtPrice = (value: number | null | undefined) => {
  if (value == null || value === 0) return "—";
  return value >= 100 ? value.toFixed(2) : value.toFixed(5);
};

const BrokerPositions = () => {
  const { account, accountLoaded, positions, lastCommand, connect, disconnect } =
    useBrokerStore();
  const [closing, setClosing] = useState<Record<number, boolean>>({});
  const [confirming, setConfirming] = useState<number | null>(null);

  useEffect(() => {
    connect();
    return () => disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Surface close results (the row itself disappears via the positions event).
  useEffect(() => {
    if (!lastCommand || lastCommand.type !== "close_position") return;
    const ticket = lastCommand.position_ticket;
    if (!ticket || !closing[ticket]) return;
    setClosing((current) => ({ ...current, [ticket]: false }));
    if (lastCommand.status === "filled") {
      toast.success(`Closed #${ticket} @ ${fmtPrice(lastCommand.result_price)}`);
    } else {
      toast.error(
        `Close ${lastCommand.status}: ${lastCommand.error_text || "no reason given"}`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastCommand]);

  const requestClose = async (ticket: number) => {
    setConfirming(null);
    setClosing((current) => ({ ...current, [ticket]: true }));
    try {
      await closePosition(ticket, newIdempotencyKey());
    } catch (error: any) {
      setClosing((current) => ({ ...current, [ticket]: false }));
      toast.error(error?.response?.data?.detail || "Close failed to submit");
    }
  };

  if (!accountLoaded) {
    return <div className="p-4 text-xs text-gray-400">Loading broker connection…</div>;
  }
  if (!account) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Connect your MetaTrader account to see open positions.
        </p>
        <Link
          to="/settings/broker"
          className="rounded-md bg-squawk-blue px-3 py-1.5 text-xs font-semibold text-white hover:bg-squawk-blue-light transition"
        >
          Connect a broker
        </Link>
      </div>
    );
  }

  const totalProfit = positions.reduce((sum, position) => sum + position.profit, 0);

  return (
    <div className="flex h-full flex-col text-gray-900 dark:text-white">
      {!account.connected && (
        <div className="m-2 rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
          Terminal offline — positions shown as of last connection.
        </div>
      )}

      {positions.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6 text-sm text-gray-500 dark:text-gray-400">
          No open positions
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <table className="min-w-full text-xs">
            <thead className="sticky top-0 bg-white dark:bg-gray-900 text-1.5xs text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium">Symbol</th>
                <th className="px-2 py-1.5 text-left font-medium">Side</th>
                <th className="px-2 py-1.5 text-right font-medium">Lots</th>
                <th className="px-2 py-1.5 text-right font-medium">Open</th>
                <th className="px-2 py-1.5 text-right font-medium">SL / TP</th>
                <th className="px-2 py-1.5 text-right font-medium">P/L</th>
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-white/5">
              {positions.map((position) => (
                <tr key={position.ticket}>
                  <td className="px-2 py-1.5 font-semibold">{position.symbol}</td>
                  <td
                    className={clsx(
                      "px-2 py-1.5 font-medium uppercase",
                      position.side === "buy" ? "text-green-500" : "text-red-500"
                    )}
                  >
                    {position.side}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{position.lots}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {fmtPrice(position.open_price)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-500 dark:text-gray-400">
                    {fmtPrice(position.sl)} / {fmtPrice(position.tp)}
                  </td>
                  <td
                    className={clsx(
                      "px-2 py-1.5 text-right font-semibold tabular-nums",
                      position.profit >= 0 ? "text-green-500" : "text-red-500"
                    )}
                  >
                    {position.profit.toFixed(2)}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {confirming === position.ticket ? (
                      <span className="inline-flex gap-1">
                        <button
                          type="button"
                          onClick={() => requestClose(position.ticket)}
                          className="rounded bg-red-500 px-2 py-0.5 text-1.5xs font-semibold text-white hover:bg-red-600"
                        >
                          Confirm
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirming(null)}
                          className="rounded bg-gray-200 dark:bg-white/10 px-2 py-0.5 text-1.5xs"
                        >
                          ✕
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        disabled={!account.connected || closing[position.ticket]}
                        onClick={() => setConfirming(position.ticket)}
                        className="rounded bg-gray-100 dark:bg-white/10 px-2 py-0.5 text-1.5xs font-medium hover:bg-gray-200 dark:hover:bg-white/20 disabled:opacity-40"
                      >
                        {closing[position.ticket] ? "Closing…" : "Close"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-between border-t border-gray-200 dark:border-white/10 px-3 py-2 text-1.5xs">
        <span className="text-gray-500 dark:text-gray-400">
          {positions.length} open position{positions.length === 1 ? "" : "s"}
        </span>
        <span
          className={clsx(
            "font-semibold tabular-nums",
            totalProfit >= 0 ? "text-green-500" : "text-red-500"
          )}
        >
          {totalProfit.toFixed(2)}
          {account.state ? ` ${account.state.currency}` : ""}
        </span>
      </div>
    </div>
  );
};

export default BrokerPositions;
